import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function PDFContent() {
  const [pdfs, setPdfs] = useState([]);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPDFs();
  }, []);

  const loadPDFs = async () => {
    try {
      const { data } = await api.get('/documents/pdf-list');
      setPdfs(data);
    } catch (err) {
      toast.error('Failed to load PDFs');
    } finally {
      setLoading(false);
    }
  };

  const regeneratePDF = async (bookingId) => {
    try {
      const { data } = await api.post(`/documents/pdf-receipt/${bookingId}`);
      toast.success('PDF regenerated!');
      setSelectedPdf(prev => prev ? { ...prev, pdfUrl: data.pdfUrl } : null);
      loadPDFs();
    } catch (err) {
      toast.error('Failed to regenerate PDF');
    }
  };

  const filteredPdfs = pdfs.filter(p =>
    p.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.bookingId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.trekName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">📄 PDF Content Manager</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar - PDF List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 focus:ring-2 focus:ring-primary-500 outline-none"
              placeholder="Search receipts..." />

            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {loading ? (
                <p className="text-center text-gray-400 py-8">Loading...</p>
              ) : filteredPdfs.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No PDFs generated yet</p>
              ) : (
                filteredPdfs.map(pdf => (
                  <button key={pdf.bookingId} onClick={() => setSelectedPdf(pdf)}
                    className={`w-full text-left p-3 rounded-lg border text-sm transition ${
                      selectedPdf?.bookingId === pdf.bookingId ? 'bg-primary-50 border-primary-300' : 'hover:bg-gray-50 border-gray-100'
                    }`}>
                    <p className="font-medium text-gray-900">{pdf.customerName}</p>
                    <p className="text-xs text-gray-500">{pdf.bookingId} • {pdf.trekName}</p>
                    <p className="text-xs text-gray-400 mt-1">{new Date(pdf.generatedAt).toLocaleDateString()}</p>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Main - PDF Preview */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border p-6">
            {selectedPdf ? (
              <>
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-lg font-semibold">{selectedPdf.customerName}</h2>
                    <p className="text-sm text-gray-500">{selectedPdf.bookingId} • {selectedPdf.trekName}</p>
                  </div>
                  <div className="space-x-2">
                    <button onClick={() => regeneratePDF(selectedPdf.bookingId)}
                      className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-purple-700">
                      🔄 Regenerate
                    </button>
                    <a href={selectedPdf.pdfUrl} target="_blank" rel="noopener noreferrer"
                      className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700 inline-block">
                      ⬇️ Download
                    </a>
                  </div>
                </div>
                <iframe src={selectedPdf.pdfUrl} className="w-full h-[600px] border rounded-lg" title="PDF Preview" />
              </>
            ) : (
              <div className="h-[600px] flex items-center justify-center text-gray-400">
                Select a receipt from the list to preview
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
